import React, { useState, useEffect, useCallback } from 'react';
import './OfficesList.scss';
import SortTable from '../SortTable';
import ListResults from '../ListResults';
import Icon from '../Icon';
import OfficeModal from './OfficeModal';
import { getData, manageData } from '../../requests/CRUD';
import { OFFICES } from '../../consts/tables';
import plus from '../../svgs/plus-white.svg';
import svgEdit from '../../svgs/edit.svg';
import svgTrash from '../../svgs/Trash.svg';
import { applySort } from '../../sort-utils';

const limit = 500;

const initialAddForm = {
    NAME: '',
    ADDRESS: '',
    CITY: '',
    COUNTRY: '',
    STATE: '',
    ZIP: '',
};

export default function OfficesList(props) {
    const { location } = props;
    const query = location.query || {};

    const [items, setItems] = useState(null);
    const [totalCount, setTotalCount] = useState(null);
    const [error, setError] = useState(null);
    const [isBusy, setIsBusy] = useState(null);
    const [addModalOpen, setAddModalOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [addForm, setAddForm] = useState(initialAddForm);
    const [saveError, setSaveError] = useState(null);
    const [saveBusy, setSaveBusy] = useState(false);

    const fetchOffices = useCallback((queryOrder) => {
        setIsBusy(true);
        setError(null);
        const options = {
            limit: { count: limit, offset: 0 },
            filter: [
                { code: 'DELETED', text: '', type: 'null' },
                { code: 'DELETED', text: '0', type: 'exact' },
            ],
            filterType: 'or',
        };
        getData({ tableCode: OFFICES, options })
            .then((res) => {
                const raw = (res && res.data) || [];
                const filtered = raw.filter((row) => row.DELETED !== 1 && row.DELETED !== '1');
                const normalized = filtered.map((row) => ({
                    ...row,
                    ARCIMEDES_ROW_ID: row.ARCIMEDES_ROW_ID != null ? String(row.ARCIMEDES_ROW_ID) : row.ARCIMEDES_ROW_ID,
                }));
                setItems(applySort(normalized, queryOrder));
                setTotalCount(normalized.length);
                setError(res && res.success === false ? (res.message || 'Failed to load') : null);
            })
            .catch((err) => {
                setItems([]);
                setTotalCount(0);
                setError(err && err.message ? err.message : 'Failed to load offices');
            })
            .finally(() => setIsBusy(null));
    }, []);

    // Fetch only on mount so the spinner stops after first load.
    useEffect(() => {
        fetchOffices(query.order);
    }, [fetchOffices]);

    // Re-sort client-side when sort order changes (stable deps to avoid loop).
    const orderKey = Array.isArray(query.order) ? query.order.join(',') : (query.order || '');
    useEffect(() => {
        if (items == null) return;
        setItems((prev) => applySort(prev || [], query.order));
    }, [orderKey]);

    const openAddModal = useCallback(() => {
        setEditingId(null);
        setAddForm(initialAddForm);
        setAddModalOpen(true);
        setSaveError(null);
        setSaveBusy(false);
    }, []);

    const openEditModal = useCallback((row) => {
        setEditingId(row.ARCIMEDES_ROW_ID);
        setAddForm({
            NAME: row.NAME,
            ADDRESS: row.ADDRESS,
            CITY: row.CITY,
            STATE: row.STATE,
            COUNTRY: row.COUNTRY,
            ZIP: row.ZIP,
        });
        setAddModalOpen(true);
        setSaveError(null);
        setSaveBusy(false);
    }, []);

    const closeAddModal = useCallback(() => {
        setAddModalOpen(false);
        setEditingId(null);
        setSaveError(null);
    }, []);

    const handleDelete = useCallback(
        (row) => {
            manageData({
                tableCode: OFFICES,
                data: [{ ARCIMEDES_ROW_ID: row.ARCIMEDES_ROW_ID, DELETED: 1 }],
            }).then((res) => {
                if (res && res.success) fetchOffices(query.order);
            });
        },
        [fetchOffices, query.order]
    );

    const handleAddFormChange = useCallback((field, value) => {
        setAddForm((prev) => ({ ...prev, [field]: value }));
    }, []);

    const handleAddSubmit = useCallback(
        (e) => {
            e.preventDefault();
            if (saveBusy) return;
            setSaveBusy(true);
            setSaveError(null);
            const payload = {
                NAME: addForm.NAME || '',
                ADDRESS: addForm.ADDRESS || '',
                CITY: addForm.CITY || '',
                COUNTRY: addForm.COUNTRY || '',
                STATE: addForm.STATE || '',
                ZIP: addForm.ZIP || '',
            };
            if (editingId) payload.ARCIMEDES_ROW_ID = editingId;
            manageData({
                tableCode: OFFICES,
                data: [payload],
            })
                .then((res) => {
                    if (res && res.success) {
                        closeAddModal();
                        fetchOffices(query.order);
                    } else {
                        setSaveError((res && res.message) || 'Failed to save office');
                        setSaveBusy(false);
                    }
                })
                .catch((err) => {
                    setSaveError(err && err.message ? err.message : 'Failed to save office');
                    setSaveBusy(false);
                });
        },
        [addForm, editingId, saveBusy, closeAddModal, fetchOffices, query.order]
    );

    const columns = React.useMemo(
        () => [
            { code: 'NAME', name: 'Name', columnStyle: { minWidth: 150 }, render: (el) => el.NAME },
            { code: 'ADDRESS', name: 'Address', columnStyle: { minWidth: 180 }, render: (el) => el.ADDRESS},
            { code: 'CITY', name: 'City', columnStyle: { minWidth: 120 }, render: (el) => el.CITY },
            { code: 'STATE', name: 'State', columnStyle: { minWidth: 80 }, render: (el) => el.STATE },
            { code: 'COUNTRY', name: 'Country', columnStyle: { minWidth: 120 }, render: (el) => el.COUNTRY },
            { code: 'ZIP', name: 'Zip', columnStyle: { minWidth: 90 }, render: (el) => el.ZIP },
            {
                name: '',
                columnStyle: { width: 90, minWidth: 90 },
                render: (el) => (
                    <span className="OfficesList_actions">
                        <button type="button" className="button-new text" onClick={() => openEditModal(el)} title="Edit">
                            <Icon svg={svgEdit} />
                        </button>
                        <button type="button" className="button-new text negative" onClick={() => handleDelete(el)} title="Delete">
                            <Icon svg={svgTrash} />
                        </button>
                    </span>
                ),
            },
        ],
        [openEditModal, handleDelete]
    );

    return (
        <div className="OfficesList" data-is-busy={isBusy}>
            <div className="page-rounded-corner-container">
                <div className="page-header-container list">
                    <div className="page-header list" style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', width: '100%' }}>
                            <div className="page-title">Offices</div>
                            <button type="button" className="button-new plus positive" onClick={openAddModal}>
                                <Icon svg={plus} />
                            </button>
                        </div>
                    </div>
                    <div className="line" />
                </div>
            </div>
            <div className="page-body-container">
                <SortTable columns={columns} items={items || []} query={query} />
                <ListResults
                    query={query}
                    pathname={location.pathname}
                    error={error}
                    totalCount={totalCount}
                    itemCount={items ? items.length : false}
                    limit={limit}
                />
            </div>
            <OfficeModal
                show={addModalOpen}
                onClose={closeAddModal}
                form={addForm}
                onFormChange={handleAddFormChange}
                onSubmit={handleAddSubmit}
                editingId={editingId}
                saveError={saveError}
                saveBusy={saveBusy}
            />
        </div>
    );
}
